/**
 * DistortionModule — WaveShaper-based saturation / overdrive.
 *
 * Graph:
 *
 *   input ─┬─ dryGain ─────────────────────────────────────► outputMix ─►
 *          └─ preGain ─► waveShaper ─► tone LP ─► wetGain ─► outputMix
 *
 * Drive scales the waveshaper curve's sharpness (tanh k) and the preGain
 * that hits it, so turning Drive up both sharpens the curve AND pushes more
 * signal into it — what you'd expect from a guitar pedal's drive knob.
 * Tone is a post-clip lowpass; winding it down rolls off the fizz.
 * Mix is an equal-power crossfade between dry and wet.
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

const CURVE_SAMPLES = 1024;

function buildDriveCurve(drive: number): Float32Array<ArrayBuffer> {
  // Drive 0 → near-linear (k ≈ 1). Drive 1 → aggressive clip (k ≈ 50).
  const k = 1 + drive * 50;
  // Allocate with an explicit ArrayBuffer to satisfy WaveShaperNode.curve's
  // newer TS signature (Float32Array<ArrayBuffer> specifically).
  const out = new Float32Array(new ArrayBuffer(CURVE_SAMPLES * 4));
  for (let i = 0; i < CURVE_SAMPLES; i++) {
    const x = (i / (CURVE_SAMPLES - 1)) * 2 - 1; // -1..+1
    out[i] = Math.tanh(k * x);
  }
  return out;
}

export class DistortionModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'distortion' as const;
  readonly displayName = 'DIST';
  readonly id: string;
  bypass = false;

  readonly drive = new NumericParameter({
    name: 'Drive',
    default: 0.3,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 30,
    // Bias moderate — full drive on every roll is abusive.
    randomSkew: 0.6,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly tone = new NumericParameter({
    name: 'Tone',
    default: 6000,
    min: 200,
    max: 20000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 15,
    // Bias high — same rationale as filter cutoff.
    randomSkew: 3,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(0)}`,
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 1,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    // Slight low bias so random patches aren't always drenched in distortion.
    randomSkew: 0.7,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [this.drive, this.tone, this.mix];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private waveshaper: WaveShaperNode | null = null;
  private preGain: GainNode | null = null;
  private tonePostFilter: BiquadFilterNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('dist');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.preGain = ctx.createGain();
    this.waveshaper = ctx.createWaveShaper();
    this.waveshaper.oversample = '2x';
    this.waveshaper.curve = buildDriveCurve(this.drive.value);

    this.tonePostFilter = ctx.createBiquadFilter();
    this.tonePostFilter.type = 'lowpass';
    this.tonePostFilter.frequency.value = this.tone.value;
    this.tonePostFilter.Q.value = 0.5; // gentle

    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    // Wet path: input → preGain → waveshaper → tone → wetGain
    this.inputNode.connect(this.preGain);
    this.preGain.connect(this.waveshaper);
    this.waveshaper.connect(this.tonePostFilter);
    this.tonePostFilter.connect(this.wetGain);
    this.wetGain.connect(this.outputMixNode);

    // Dry path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputMixNode);

    this.applyPreGain(this.drive.value);
    this.applyMix(this.mix.value, /* instant */ true);

    this.tone.bindAudioParam(this.tonePostFilter.frequency, ctx);

    // Drive changes the curve (not an AudioParam) and the preGain.
    this.unsubs.push(
      this.drive.subscribe((v) => {
        if (this.waveshaper) this.waveshaper.curve = buildDriveCurve(v);
        this.applyPreGain(v);
      }),
    );
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));

    this.started = true;
  }

  /**
   * Mod-routing lookup — returns the AudioParam to modulate for a given
   * parameter name. Only Tone has a native AudioParam; Drive reshapes the
   * waveshaper curve on change (not AudioParam-compatible) and Mix drives
   * a pair of gain nodes via setTargetAtTime.
   */
  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Tone') return this.tonePostFilter?.frequency ?? null;
    return null;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.inputNode?.disconnect();
    this.preGain?.disconnect();
    this.waveshaper?.disconnect();
    this.tonePostFilter?.disconnect();
    this.dryGain?.disconnect();
    this.wetGain?.disconnect();
    this.outputMixNode?.disconnect();
    this.inputNode = null;
    this.outputMixNode = null;
    this.waveshaper = null;
    this.preGain = null;
    this.tonePostFilter = null;
    this.dryGain = null;
    this.wetGain = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }

  private applyPreGain(drive: number): void {
    if (!this.engine || !this.preGain) return;
    // Push ~10× at full drive; subtle push at low drive.
    const g = 1 + drive * 9;
    const t = this.engine.context.currentTime;
    this.preGain.gain.cancelScheduledValues(t);
    this.preGain.gain.setTargetAtTime(g, t, 0.02);
  }

  private applyMix(mix: number, instant = false): void {
    if (!this.engine || !this.dryGain || !this.wetGain) return;
    // Equal-power crossfade.
    const dry = Math.cos((mix * Math.PI) / 2);
    const wet = Math.sin((mix * Math.PI) / 2);
    const t = this.engine.context.currentTime;
    if (instant) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.02);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.02);
    }
  }
}
